// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Constants } from '../../../Constants';
import { ServiceInfo } from '../../../libs/models/ServiceInfo';
import {
  ActiveUserInfo,
  Alert,
  AlertModalMessage,
  AlertToast,
  AppConnectState,
  AppState,
  FeatureKeys,
  initialState,
} from '../../../libs/models/app/AppState';
import { AlertType } from '../../../libs/models/AlertType';

// (6.21)Alert : 기존 ui / toast alert  -> 삭제  --> 업데이트 메시지때문에 남겨둠 -> 나중에 삭해야함
// AlertToast : new 토스트 alert
// AlertModal : new 전체 화면 alert

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setAppConnectState: (state: AppState, action: PayloadAction<AppConnectState>) => {
      state.appConnectState = action.payload;
    },
    setMaintenance: (state: AppState, action: PayloadAction<boolean>) => {
      state.isMaintenance = action.payload;
    },
    setAlerts: (state: AppState, action: PayloadAction<Alert[]>) => {
      state.alerts = action.payload;
    },
    addAlert: (state: AppState, action: PayloadAction<Alert>) => {
      if (action.payload.id == Constants.app.CONNECTION_ALERT_ID || isServerConnectionError(action.payload.message)) {
        // CONNECTION_ALERT인 경우의 텍스트 업데이트
        updateConnectionStatus(state, action.payload);
      } else {
        // CONNECTION_ALERT 이외
        addNewAlert(state.alerts, action.payload);
      }
    },
    removeAlert: (state: AppState, action: PayloadAction<number>) => {
      state.alerts.splice(action.payload, 1);
    },
    showAlertModal: (state: AppState, action: PayloadAction<AlertModalMessage>) => {
      state.altModalMessage = action.payload;
      state.altModalVisible = true;
    },
    hideAlertModal: (state: AppState) => {
      state.altModalVisible = false;
    },

    showAlertToast: (state: AppState, action: PayloadAction<AlertToast>) => {
      if (isServerConnectionError(action.payload.altMessage)) {
        state.toastMessage.push({
          altMessage: '네트워크에 문제가 생겼어요. 상단 새로고침 버튼을 눌러주세요.',
          altType: AlertType.Warning,
        });
        state.toastVisible = true;
      } else {
        state.toastMessage.push(action.payload);
        state.toastVisible = true;
      }
    },
    hideAlertToast: (state) => {
      state.toastVisible = false;
    },
    removeAlertToast: (state) => {
      state.toastMessage.shift();
      state.toastVisible = state.toastMessage.length > 0;
    },

    setActiveUserInfo: (state: AppState, action: PayloadAction<ActiveUserInfo>) => {
      state.activeUserInfo = action.payload;
    },
    // This sets the feature flag based on end user input
    toggleFeatureFlag: (state: AppState, action: PayloadAction<FeatureKeys>) => {
      const feature = state.features[action.payload];
      state.features = {
        ...state.features,
        [action.payload]: {
          ...feature,
          enabled: !feature.enabled,
        },
      };
    },
    // This controls feature availability based on the state of backend
    toggleFeatureState: (
      state: AppState,
      action: PayloadAction<{
        feature: FeatureKeys;
        deactivate: boolean;
        enable: boolean;
      }>,
    ) => {
      const feature = state.features[action.payload.feature];
      state.features = {
        ...state.features,
        [action.payload.feature]: {
          ...feature,
          enabled: action.payload.deactivate ? false : action.payload.enable,
          inactive: action.payload.deactivate,
        },
      };
    },
    setServiceInfo: (state: AppState, action: PayloadAction<ServiceInfo>) => {
      state.serviceInfo = action.payload;
    },
    setAuthConfig: (state: AppState, action: PayloadAction<AppState['authConfig']>) => {
      state.authConfig = action.payload;
    },
    setViewType: (state: AppState, action: PayloadAction<string>) => {
      state.viewType = action.payload;
    },
    showSettingDialog: (state: AppState, action: PayloadAction<{visible: boolean, type: number}>) => {
      state.settingDialogVisible = action.payload.visible;
      state.settingDialogType = action.payload.type;
    }
  },
});

export const {
  addAlert,
  removeAlert,
  setAlerts,
  showAlertModal,
  hideAlertModal,
  showAlertToast,
  hideAlertToast,
  removeAlertToast,
  setActiveUserInfo,
  toggleFeatureFlag,
  toggleFeatureState,
  setServiceInfo,
  setMaintenance,
  setAuthConfig,
  setAppConnectState,
  setViewType,
  showSettingDialog
} = appSlice.actions;

export default appSlice.reducer;

const isServerConnectionError = (message: string) => {
  console.log(message);
  return (
    message.includes(`Cannot send data if the connection is not in the 'Connected' State.`) ||
    message.includes(`Server timeout elapsed without receiving a message from the server.`)
  );
};

const addNewAlert = (alerts: Alert[], newAlert: Alert) => {
  if (alerts.length === 3) {
    alerts.shift();
  }
  alerts.push(newAlert);
};

const updateConnectionStatus = (state: AppState, statusUpdate: Alert) => {
  if (isServerConnectionError(statusUpdate.message)) {
    statusUpdate.message =
      // Constant message so alert UI doesn't feel glitchy on every connection error from SignalR
      '네트워크에 문제가 생겼어요. 상단 새로고침 버튼을 눌러주세요.';
  }

  // There should only ever be one connection alert at a time,
  // so we tag the alert with a unique ID so we can remove if needed
  statusUpdate.id ??= Constants.app.CONNECTION_ALERT_ID;

  // Remove the existing connection alert if it exists
  const connectionAlertIndex = state.alerts.findIndex((alert) => alert.id === Constants.app.CONNECTION_ALERT_ID);
  if (connectionAlertIndex !== -1) {
    state.alerts.splice(connectionAlertIndex, 1);
  }
  showAlertToast({ altMessage: '테스트', altType: AlertType.Warning });
  if (statusUpdate.type !== AlertType.Success)
    showAlertToast({ altMessage: statusUpdate.message, altType: AlertType.Warning });
};
